/**
 * URL search helpers for admin Analytics (#794).
 * All config is optional so bare `/analytics` links stay valid.
 */

export const ANALYTICS_WIDGET_IDS = [
  'users',
  'songs',
  'contributions',
  'jams',
  'reports',
  'review_outcomes',
  'entitlements',
] as const

export type AnalyticsWidgetId = (typeof ANALYTICS_WIDGET_IDS)[number]

export const ANALYTICS_WIDGET_LABELS: Record<AnalyticsWidgetId, string> = {
  users: 'New users',
  songs: 'Published songs',
  contributions: 'Contributions',
  jams: 'Jams started',
  reports: 'Reports filed',
  review_outcomes: 'Review outcomes',
  entitlements: 'Entitlement grants',
}

export const ANALYTICS_RANGE_PRESETS = [7, 30, 90, 180] as const
export type AnalyticsRangePreset = (typeof ANALYTICS_RANGE_PRESETS)[number]

export const REPORT_STATUS_OPTIONS = ['open', 'dismissed', 'actioned'] as const
export type AnalyticsReportStatus = (typeof REPORT_STATUS_OPTIONS)[number]

export const REPORT_REASON_OPTIONS = [
  'copyright',
  'spam',
  'abuse',
  'other',
] as const
export type AnalyticsReportReason = (typeof REPORT_REASON_OPTIONS)[number]

export const ENTITLEMENT_SOURCE_OPTIONS = [
  'polar',
  'manual_grant',
  'comp',
  'trial',
  'honorarium',
] as const
export type AnalyticsEntitlementSource =
  (typeof ENTITLEMENT_SOURCE_OPTIONS)[number]

export type AnalyticsSearch = {
  /** Lookback days (1–180). Omitted → 30. */
  range?: number
  /**
   * Visible widgets, comma-separated ids.
   * Omitted → all widgets visible.
   */
  widgets?: string
  report_status?: AnalyticsReportStatus
  report_reason?: AnalyticsReportReason
  entitlement_source?: AnalyticsEntitlementSource
}

const WIDGET_SET = new Set<string>(ANALYTICS_WIDGET_IDS)

export function parseAnalyticsSearch(
  search: Record<string, unknown>,
): AnalyticsSearch {
  const out: AnalyticsSearch = {}

  const rangeRaw = search.range
  const rangeN =
    typeof rangeRaw === 'string' || typeof rangeRaw === 'number'
      ? Number(rangeRaw)
      : NaN
  if (Number.isInteger(rangeN) && rangeN >= 1 && rangeN <= 180) {
    out.range = rangeN
  }

  if (typeof search.widgets === 'string' && search.widgets.trim()) {
    const ids = search.widgets
      .split(',')
      .map((s) => s.trim())
      .filter((id): id is AnalyticsWidgetId => WIDGET_SET.has(id))
    if (ids.length > 0) {
      out.widgets = [...new Set(ids)].join(',')
    }
  }

  if (
    typeof search.report_status === 'string' &&
    (REPORT_STATUS_OPTIONS as readonly string[]).includes(search.report_status)
  ) {
    out.report_status = search.report_status as AnalyticsReportStatus
  }
  if (
    typeof search.report_reason === 'string' &&
    (REPORT_REASON_OPTIONS as readonly string[]).includes(search.report_reason)
  ) {
    out.report_reason = search.report_reason as AnalyticsReportReason
  }
  if (
    typeof search.entitlement_source === 'string' &&
    (ENTITLEMENT_SOURCE_OPTIONS as readonly string[]).includes(
      search.entitlement_source,
    )
  ) {
    out.entitlement_source =
      search.entitlement_source as AnalyticsEntitlementSource
  }

  return out
}

export function resolveAnalyticsRange(search: AnalyticsSearch): number {
  return search.range ?? 30
}

/** Ordered list of visible widgets (defaults to all; catalog order). */
export function resolveVisibleWidgets(
  search: AnalyticsSearch,
): AnalyticsWidgetId[] {
  if (!search.widgets?.trim()) {
    return [...ANALYTICS_WIDGET_IDS]
  }
  const selected = new Set(
    search.widgets
      .split(',')
      .map((s) => s.trim())
      .filter((id): id is AnalyticsWidgetId => WIDGET_SET.has(id)),
  )
  if (selected.size === 0) return [...ANALYTICS_WIDGET_IDS]
  return ANALYTICS_WIDGET_IDS.filter((id) => selected.has(id))
}

export function serializeWidgets(
  ids: readonly AnalyticsWidgetId[],
): string | undefined {
  if (ids.length === 0 || ids.length === ANALYTICS_WIDGET_IDS.length) {
    return undefined
  }
  // Preserve catalog order for stable URLs
  const set = new Set(ids)
  return ANALYTICS_WIDGET_IDS.filter((id) => set.has(id)).join(',')
}
